// ═══════════════════════════════════════════════════════════════════════════════
// AI Prodavac - Hypothesis vs Reality Component
// AI calibration visualization (predictions vs actual outcomes)
// ═══════════════════════════════════════════════════════════════════════════════

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  formatUiNumber,
  formatUiPercent,
} from '@/lib/ui-format';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface ConfidenceBucket {
  bucket: string;
  avgPredicted: number;
  avgActual: number;
  sampleSize: number;
}

interface HypothesisVsRealityProps {
  data?: {
    byConfidenceBucket: ConfidenceBucket[];
    overallCalibrationScore: number;
  };
}

interface CalibrationTooltipPayloadItem {
  name?: string;
  value?: number;
  dataKey?: string;
  payload?: {
    bucket?: string;
    predicted?: number;
    actual?: number;
    sampleSize?: number;
  };
}

interface CalibrationTooltipProps {
  active?: boolean;
  payload?: CalibrationTooltipPayloadItem[];
  label?: string;
}

const CalibrationTooltip = ({ 
  active, 
  payload, 
  label 
}: CalibrationTooltipProps): JSX.Element | null => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const predictedItem = payload.find((item) => item.dataKey === 'predicted');
  const actualItem = payload.find((item) => item.dataKey === 'actual');
  // Get sample size from the first payload item's payload property (Recharts stores full data there)
  const sampleSize = payload[0]?.payload?.sampleSize;

  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 shadow-elevation-2">
      <p className="text-xs font-semibold text-foreground mb-2">{label}</p>
      <div className="space-y-1 text-xs">
        {predictedItem && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">AI Predicted:</span>
            <span className="font-medium text-foreground">
              {predictedItem.value === undefined
                ? '—'
                : formatUiPercent(predictedItem.value / 100, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
            </span>
          </div>
        )}
        {actualItem && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Actual Outcome:</span>
            <span className="font-medium text-foreground">
              {actualItem.value === undefined
                ? '—'
                : formatUiPercent(actualItem.value / 100, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
            </span>
          </div>
        )}
        {sampleSize !== undefined && (
          <div className="flex items-center justify-between gap-3 pt-1 border-t border-border">
            <span className="text-muted-foreground">Sample Size:</span>
            <span className="font-medium text-foreground">
              {formatUiNumber(sampleSize)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const getCalibrationScoreColor = (score: number): string => {
  if (score >= 90) {
    return 'bg-status-success/20 text-status-success border-status-success';
  }
  if (score >= 70) {
    return 'bg-status-warning/20 text-status-warning border-status-warning';
  }
  return 'bg-status-danger/20 text-status-danger border-status-danger';
};

const getCalibrationScoreLabel = (score: number): string => {
  if (score >= 90) {
    return 'Excellent';
  }
  if (score >= 70) {
    return 'Good';
  }
  return 'Needs Improvement';
};

export function HypothesisVsReality({ data }: HypothesisVsRealityProps): JSX.Element {
  if (!data || !data.byConfidenceBucket || data.byConfidenceBucket.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-lg">AI Calibration</CardTitle>
          <CardDescription>
            Perfect calibration = AI predictions match reality
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No calibration data available
          </p>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.byConfidenceBucket.map((bucket) => ({
    bucket: bucket.bucket,
    predicted: bucket.avgPredicted,
    actual: bucket.avgActual,
    sampleSize: bucket.sampleSize,
  }));

  const calibrationScore = data.overallCalibrationScore ?? 0;
  const scoreColor = getCalibrationScoreColor(calibrationScore);
  const scoreLabel = getCalibrationScoreLabel(calibrationScore);

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">AI Calibration</CardTitle>
            <CardDescription className="mt-1">
              Perfect calibration = AI predictions match reality
            </CardDescription>
          </div>
          <Badge
            variant="outline"
            className={cn('text-sm font-semibold px-3 py-1', scoreColor)}
          >
            {formatUiPercent(calibrationScore / 100, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}{' '}
            - {scoreLabel}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="bucket"
              className="text-xs"
              tick={{ fill: 'var(--muted-foreground)' }}
              label={{ value: 'Confidence Buckets', position: 'insideBottom', offset: -5 }}
            />
            <YAxis
              className="text-xs"
              tick={{ fill: 'var(--muted-foreground)' }}
              domain={[0, 100]}
              label={{ value: 'Success Rate (%)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CalibrationTooltip />} />
            <Legend />
            <Bar
              dataKey="predicted"
              name="AI Predicted"
              fill="var(--color-status-info)"
              fillOpacity={0.6}
              radius={[4, 4, 0, 0]}
            />
            <Line
              type="monotone"
              dataKey="actual"
              name="Actual Outcome"
              stroke="var(--color-status-success)"
              strokeWidth={3}
              dot={{ fill: 'var(--color-status-success)', r: 5 }}
              activeDot={{ r: 7 }}
            />
          </ComposedChart>
        </ResponsiveContainer>

        <div className="mt-6 pt-4 border-t border-border">
          <div className="grid grid-cols-2 gap-3 text-xs">
            {chartData.map((item) => (
              <div
                key={item.bucket}
                className="flex items-center justify-between p-2 rounded-md bg-surface"
              >
                <span className="text-muted-foreground font-medium">
                  {item.bucket}:
                </span>
                <span className="text-foreground font-semibold">
                  n={item.sampleSize}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
